import { useEffect, useState, useCallback, useMemo } from 'react';
import { Loader2, Plus, Search, X, UserCog } from 'lucide-react';
import {
  fieldPartnersApi,
  type FieldPartner,
  type KycStatus,
  type FieldPartnerTier,
} from '@/api/field-partners';
import { formatINR } from '@/utils/formatters';
import { FieldProgramGate } from '@/components/field-partners/FieldProgramGate';
import type { FieldProgramConfig } from '@/hooks/useFieldProgramConfig';

const KYC_COLORS: Record<KycStatus, string> = {
  PENDING: 'bg-amber-50 text-amber-700',
  MANUAL_REVIEW: 'bg-orange-50 text-orange-700',
  APPROVED: 'bg-green-50 text-green-700',
  REJECTED: 'bg-red-50 text-red-700',
  RESUBMIT: 'bg-blue-50 text-blue-700',
};

const TIER_COLORS: Record<FieldPartnerTier, string> = {
  BRONZE: 'bg-orange-100 text-orange-800',
  SILVER: 'bg-gray-200 text-gray-800',
  GOLD: 'bg-yellow-100 text-yellow-800',
};

function DirectoryInner({ config }: { config: FieldProgramConfig | null }) {
  const partnerLabel = config?.partnerTypeLabel || 'Field Partner';
  const appName = config?.appName || 'Field Partners';

  const [rows, setRows] = useState<FieldPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterKyc, setFilterKyc] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', region: '', email: '' });
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fieldPartnersApi.list();
      setRows(data);
    } catch (err: any) {
      if (err.response?.status === 404) setRows([]);
      else setError(err.response?.data?.error || `Failed to load ${partnerLabel.toLowerCase()}s`);
    } finally {
      setLoading(false);
    }
  }, [partnerLabel]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const filtered = useMemo(() => {
    return rows.filter((p) => {
      if (filterKyc && p.kycStatus !== filterKyc) return false;
      if (filterTier && p.tier !== filterTier) return false;
      if (filterRegion && p.region !== filterRegion) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!p.name.toLowerCase().includes(q) && !p.phone.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, filterKyc, filterTier, filterRegion]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize],
  );

  useEffect(() => {
    if (page > pageCount) setPage(1);
  }, [pageCount, page]);

  const regions = Array.from(new Set(rows.map((r) => r.region).filter(Boolean))) as string[];

  const handleCreate = async () => {
    if (!form.name || !form.phone) {
      alert('Name and phone are required');
      return;
    }
    if (!/^\d{10}$/.test(form.phone.replace(/\D/g, '').slice(-10))) {
      alert('Enter a valid 10-digit phone');
      return;
    }
    setSaving(true);
    try {
      await fieldPartnersApi.create({
        name: form.name,
        phone: form.phone,
        region: form.region || undefined,
        email: form.email || undefined,
      });
      setShowAdd(false);
      setForm({ name: '', phone: '', region: '', email: '' });
      await fetch();
    } catch (err: any) {
      alert(err.response?.data?.error || `Failed to create ${partnerLabel.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">
            {partnerLabel} Directory
          </h1>
          <p className="text-sm text-[var(--text-muted)]">
            All registered {partnerLabel.toLowerCase()}s in the {appName} program — KYC, tier, performance
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
        >
          <Plus size={16} />
          Add {partnerLabel}
        </button>
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <div className="relative min-w-[200px] flex-1">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or phone..."
            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] py-2 pl-9 pr-4 text-sm text-[var(--text-primary)]"
          />
        </div>
        <select
          value={filterKyc}
          onChange={(e) => setFilterKyc(e.target.value)}
          className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
        >
          <option value="">All KYC</option>
          {Object.keys(KYC_COLORS).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={filterTier}
          onChange={(e) => setFilterTier(e.target.value)}
          className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
        >
          <option value="">All Tiers</option>
          {Object.keys(TIER_COLORS).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={filterRegion}
          onChange={(e) => setFilterRegion(e.target.value)}
          className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
        >
          <option value="">All Regions</option>
          {regions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)]">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          </div>
        ) : error ? (
          <div className="py-20 text-center text-sm text-red-600">{error}</div>
        ) : paged.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-[var(--text-muted)]">
            <UserCog size={40} className="mb-2" />
            <p className="text-sm">No {partnerLabel.toLowerCase()}s found</p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border-primary)] bg-[var(--bg-inset)] text-left text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">KYC</th>
                  <th className="px-4 py-3">Tier</th>
                  <th className="px-4 py-3 text-right">Installations</th>
                  <th className="px-4 py-3 text-right">Total Earnings</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-primary)]">
                {paged.map((p) => (
                  <tr key={p.id} className="hover:bg-[var(--bg-surface-hover)]">
                    <td className="px-4 py-3 font-medium text-[var(--text-primary)]">{p.name}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{p.phone}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${KYC_COLORS[p.kycStatus]}`}
                      >
                        {p.kycStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${TIER_COLORS[p.tier]}`}
                      >
                        {p.tier}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-[var(--text-primary)]">
                      {p.totalInstallations}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-[var(--text-primary)]">
                      {formatINR(p.totalEarnings)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pageCount > 1 && (
              <div className="flex items-center justify-between border-t border-[var(--border-primary)] px-4 py-2 text-xs text-[var(--text-muted)]">
                <span>
                  Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of{' '}
                  {filtered.length}
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded border border-[var(--border-primary)] px-2 py-1 disabled:opacity-40"
                  >
                    Prev
                  </button>
                  <span>
                    {page} / {pageCount}
                  </span>
                  <button
                    disabled={page === pageCount}
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    className="rounded border border-[var(--border-primary)] px-2 py-1 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] shadow-xl">
            <div className="flex items-center justify-between border-b border-[var(--border-primary)] px-4 py-3">
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">
                Add {partnerLabel}
              </h2>
              <button
                onClick={() => setShowAdd(false)}
                className="rounded p-1 text-[var(--text-muted)] hover:bg-[var(--bg-surface-hover)]"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-3 p-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">
                  Name
                </label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm text-[var(--text-primary)]"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">
                  Phone (10-digit) — OTP will be sent
                </label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm text-[var(--text-primary)]"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">
                  Region (optional)
                </label>
                <input
                  value={form.region}
                  onChange={(e) => setForm({ ...form, region: e.target.value })}
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm text-[var(--text-primary)]"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">
                  Email (optional)
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm text-[var(--text-primary)]"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-[var(--border-primary)] px-4 py-3">
              <button
                onClick={() => setShowAdd(false)}
                className="rounded-lg border border-[var(--border-primary)] px-3 py-1.5 text-sm text-[var(--text-secondary)]"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving}
                className="rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-60"
              >
                {saving ? 'Saving...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function FieldPartnerDirectoryPage() {
  return <FieldProgramGate>{(config) => <DirectoryInner config={config} />}</FieldProgramGate>;
}

export default FieldPartnerDirectoryPage;
